// RPC Configuration
// Centralized configuration for all blockchain RPC endpoints
// Primary: Public RPC endpoints (cost-saving until volume justifies upgrade)
// Fallback: QuickNode (if configured) or official RPCs

export interface RPCConfig {
  url: string;
  username?: string;
  password?: string;
  apiKey?: string;
}

export interface ChainRPCConfig {
  primary: RPCConfig;
  fallback?: RPCConfig;
}

// API Keys (from environment variables)
const API_KEYS = {
  nownodes: import.meta.env.VITE_NOWNODES_API_KEY || '719792fa-327d-44ab-910e-da590c31d14e',
  // QuickNode endpoints (each chain has its own endpoint URL)
  quicknode_eth: import.meta.env.VITE_QUICKNODE_ETH_URL || '',
  quicknode_btc: import.meta.env.VITE_QUICKNODE_BTC_URL || '',
  quicknode_bsc: import.meta.env.VITE_QUICKNODE_BSC_URL || '',
  quicknode_polygon: import.meta.env.VITE_QUICKNODE_POLYGON_URL || '',
  quicknode_avax: import.meta.env.VITE_QUICKNODE_AVAX_URL || '',
  quicknode_arb: import.meta.env.VITE_QUICKNODE_ARB_URL || '',
  quicknode_base: import.meta.env.VITE_QUICKNODE_BASE_URL || '',
  quicknode_optimism: import.meta.env.VITE_QUICKNODE_OPTIMISM_URL || '',
  quicknode_hyperliquid: import.meta.env.VITE_QUICKNODE_HYPERLIQUID_URL || '',
  quicknode_fantom: import.meta.env.VITE_QUICKNODE_FANTOM_URL || '',
  quicknode_zksync: import.meta.env.VITE_QUICKNODE_ZKSYNC_URL || '',
  quicknode_linea: import.meta.env.VITE_QUICKNODE_LINEA_URL || '',
  quicknode_scroll: import.meta.env.VITE_QUICKNODE_SCROLL_URL || '',
  quicknode_mantle: import.meta.env.VITE_QUICKNODE_MANTLE_URL || '',
  quicknode_blast: import.meta.env.VITE_QUICKNODE_BLAST_URL || '',
  quicknode_celo: import.meta.env.VITE_QUICKNODE_CELO_URL || '',
  quicknode_gnosis: import.meta.env.VITE_QUICKNODE_GNOSIS_URL || '',
  quicknode_polygon_zkevm: import.meta.env.VITE_QUICKNODE_POLYGON_ZKEVM_URL || '',
  quicknode_mode: import.meta.env.VITE_QUICKNODE_MODE_URL || '',
  quicknode_ltc: import.meta.env.VITE_QUICKNODE_LTC_URL || '',
  quicknode_doge: import.meta.env.VITE_QUICKNODE_DOGE_URL || '',
  quicknode_bch: import.meta.env.VITE_QUICKNODE_BCH_URL || '',
  quicknode_zec: import.meta.env.VITE_QUICKNODE_ZEC_URL || '',
  quicknode_dash: import.meta.env.VITE_QUICKNODE_DASH_URL || '',
  quicknode_kmd: import.meta.env.VITE_QUICKNODE_KMD_URL || '',
  quicknode_qtum: import.meta.env.VITE_QUICKNODE_QTUM_URL || '',
};

// RPC Endpoints Configuration
// Primary: Public RPC endpoints (cost-saving until volume justifies upgrade)
// Fallback: QuickNode (if configured) or official RPCs
export const VPS_RPC_CONFIG = {
  // MM2/KDF RPC (Komodo DeFi Framework) - On VPS
  mm2: {
    url: 'https://mm2.kawswap.io',
    userpass: 'finalpass2025',
  },
  
  // Ravencoin - Private VPS Node with HTTPS (fully synced with wallet access)
  ravencoin: {
    url: 'https://rvn-rpc-mainnet.ting.finance/rpc', // Using working public endpoint
    primary: 'https://rvn-rpc-mainnet.ting.finance/rpc', // Working public RPC endpoint
    fallback: 'https://rvn.kawswap.io/rpc', // VPS endpoint (currently returning 404)
    username: 'rpcuser',
    password: 'rpcpass',
  },
  
  // Ethereum - Public RPC primary
  ethereum: {
    primary: 'https://eth.llamarpc.com',
    fallback: API_KEYS.quicknode_eth || 'https://eth.llamarpc.com',
  },
  
  // Bitcoin - Public RPC primary (MM2 requires JSON-RPC, not REST API)
  // Note: Blockstream.info is REST API, MM2 needs JSON-RPC endpoint
  bitcoin: {
    primary: API_KEYS.quicknode_btc || 'https://btc.llamarpc.com',
    fallback: 'https://btc.llamarpc.com',
  },
  
  // Binance Smart Chain - Public RPC primary
  binanceSmartChain: {
    primary: 'https://bsc.publicnode.com',
    fallback: API_KEYS.quicknode_bsc || 'https://bsc-dataseed.binance.org/',
  },
  
  // Polygon - Public RPC primary
  polygon: {
    primary: 'https://polygon.llamarpc.com',
    fallback: API_KEYS.quicknode_polygon || 'https://polygon-rpc.com/',
  },
  
  // Polygon zkEVM - Public RPC primary (separate from Polygon mainnet)
  polygonZkEVM: {
    primary: 'https://zkevm-rpc.com',
    fallback: API_KEYS.quicknode_polygon_zkevm || 'https://polygon-zkevm.publicnode.com',
  },
  
  // Avalanche - Public RPC primary
  avalanche: {
    primary: 'https://avax.publicnode.com',
    fallback: API_KEYS.quicknode_avax || 'https://api.avax.network/ext/bc/C/rpc',
  },
  
  // Arbitrum - Public RPC primary
  arbitrum: {
    primary: 'https://arbitrum.llamarpc.com',
    fallback: API_KEYS.quicknode_arb || 'https://arb1.arbitrum.io/rpc',
  },
  
  // Base - Public RPC primary
  base: {
    primary: 'https://base.llamarpc.com',
    fallback: API_KEYS.quicknode_base || 'https://mainnet.base.org',
  },
  
  // Optimism - Public RPC primary
  optimism: {
    primary: 'https://optimism.llamarpc.com',
    fallback: API_KEYS.quicknode_optimism || 'https://mainnet.optimism.io',
  },
  
  // Hyperliquid (HYPE) - Public RPC primary (rate limited: 100 req/min)
  hyperliquid: {
    primary: 'https://rpc.hyperliquid.xyz/evm',
    fallback: API_KEYS.quicknode_hyperliquid || 'https://rpc.hyperliquid.xyz/evm',
  },
  
  // Fantom - Public RPC primary
  fantom: {
    primary: 'https://rpc.ftm.tools',
    fallback: API_KEYS.quicknode_fantom || 'https://fantom.publicnode.com',
  },
  
  // zkSync Era - Public RPC primary
  zkSyncEra: {
    primary: 'https://mainnet.era.zksync.io',
    fallback: API_KEYS.quicknode_zksync || 'https://zksync-era.publicnode.com',
  },
  
  // Linea - Public RPC primary
  linea: {
    primary: 'https://rpc.linea.build',
    fallback: API_KEYS.quicknode_linea || 'https://linea.publicnode.com',
  },
  
  // Scroll - Public RPC primary
  scroll: {
    primary: 'https://rpc.scroll.io',
    fallback: API_KEYS.quicknode_scroll || 'https://scroll.publicnode.com',
  },
  
  // Mantle - Public RPC primary
  mantle: {
    primary: 'https://rpc.mantle.xyz',
    fallback: API_KEYS.quicknode_mantle || 'https://mantle.publicnode.com',
  },
  
  // Blast - Public RPC primary
  blast: {
    primary: 'https://rpc.blast.io',
    fallback: API_KEYS.quicknode_blast || 'https://blast.publicnode.com',
  },
  
  // Celo - Public RPC primary
  celo: {
    primary: 'https://forno.celo.org',
    fallback: API_KEYS.quicknode_celo || 'https://celo.publicnode.com',
  },
  
  // Gnosis Chain - Public RPC primary
  gnosis: {
    primary: 'https://rpc.gnosischain.com',
    fallback: API_KEYS.quicknode_gnosis || 'https://gnosis.publicnode.com',
  },
  
  // Mode Network - Public RPC primary
  mode: {
    primary: 'https://mainnet.mode.network',
    fallback: API_KEYS.quicknode_mode || 'https://mode.publicnode.com',
  },
  
  // Solana - Public RPC primary (for wallet/NFT viewing, future HTLC sales)
  solana: {
    primary: 'https://api.mainnet-beta.solana.com',
    fallback: 'https://rpc.ankr.com/solana',
  },
  
  icp: {
    primary: 'https://ic0.app', // ICP boundary node
    fallback: 'https://icp-api.io',
  },
  
  // Litecoin - Public RPC primary (MM2 support)
  litecoin: {
    primary: 'https://ltc.llamarpc.com',
    fallback: API_KEYS.quicknode_ltc || 'https://litecoinspace.org/api',
  },
  
  // Dogecoin - Public RPC primary (MM2 support)
  dogecoin: {
    primary: 'https://doge.llamarpc.com',
    fallback: API_KEYS.quicknode_doge || 'https://dogechain.info/api/v1',
  },
  
  // Bitcoin Cash - Public RPC primary (MM2 support)
  bitcoinCash: {
    primary: 'https://bch.llamarpc.com',
    fallback: API_KEYS.quicknode_bch || 'https://bch.llamarpc.com',
  },
  
  // Zcash - Public RPC primary (MM2 support)
  zcash: {
    primary: 'https://zcash.llamarpc.com',
    fallback: API_KEYS.quicknode_zec || 'https://zcash.llamarpc.com',
  },
  
  // Dash - Public RPC primary (MM2 support)
  dash: {
    primary: 'https://dash.llamarpc.com',
    fallback: API_KEYS.quicknode_dash || 'https://dash.llamarpc.com',
  },
  
  // Komodo (KMD) - Public RPC primary (MM2 native chain)
  komodo: {
    primary: 'https://kmd.llamarpc.com',
    fallback: API_KEYS.quicknode_kmd || 'https://kmd.llamarpc.com',
  },
  
  // Qtum - Public RPC primary (MM2 support)
  qtum: {
    primary: 'https://qtum.llamarpc.com',
    fallback: API_KEYS.quicknode_qtum || 'https://qtum.llamarpc.com',
  },
} as const;

// Get RPC endpoint for a chain (returns primary if available, otherwise fallback)
export function getRPCEndpoint(chain: keyof typeof VPS_RPC_CONFIG): string {
  const config = VPS_RPC_CONFIG[chain];
  if (!config) return '';
  
  // For chains with primary/fallback structure
  if ('primary' in config) {
    return config.primary || config.fallback || '';
  }
  
  // For chains with direct url (mm2, ravencoin)
  if ('url' in config) {
    return config.url || '';
  }
  
  return '';
}

// Get RPC endpoint with fallback support (returns [primary, fallback])
export function getRPCEndpointWithFallback(chain: keyof typeof VPS_RPC_CONFIG): [string, string] {
  const config = VPS_RPC_CONFIG[chain];
  if (!config) return ['', ''];
  
  // For chains with primary/fallback structure
  if ('primary' in config) {
    return [config.primary || '', config.fallback || ''];
  }
  
  // For chains with direct url (mm2) - no fallback
  // Ravencoin has primary/fallback structure, so it's handled above
  if ('url' in config) {
    return [config.url || '', ''];
  }
  
  return ['', ''];
}

// Get Ravencoin RPC config (for MM2 and direct RPC calls)
export function getRavencoinRPCConfig(): RPCConfig {
  return {
    url: VPS_RPC_CONFIG.ravencoin.url,
    username: VPS_RPC_CONFIG.ravencoin.username,
    password: VPS_RPC_CONFIG.ravencoin.password,
  };
}

// Get MM2 RPC config
export function getMM2Config(): RPCConfig {
  return {
    url: VPS_RPC_CONFIG.mm2.url,
    apiKey: VPS_RPC_CONFIG.mm2.userpass,
  };
}

